/**
 * useVfsDelete
 *
 * Single-shot delete for a VFS path scoped to one owner. The backend
 * emits `vfs.file.deleted` on success, which `useVfsFiles` picks up to
 * remove the row from the list — so callers usually just need to await
 * this hook and surface errors.
 *
 * `deleteFolder` is the recursive sibling: it hits the backend's
 * `delete-folder` endpoint which deletes every file under a prefix and
 * emits one `vfs.file.deleted` event per file (same client-side cleanup
 * path as the single-file delete).
 */

import { useCallback, useState } from 'react';
import { vfsApi } from '../services/api';

export interface UseVfsDeleteResult {
  deleteFile: (path: string) => Promise<boolean>;
  deleteFolder: (prefix: string) => Promise<number | null>;
  isDeleting: boolean;
  error: Error | null;
}

export function useVfsDelete(ownerId: string | undefined): UseVfsDeleteResult {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteFile = useCallback(
    async (path: string): Promise<boolean> => {
      if (!ownerId) return false;
      setIsDeleting(true);
      setError(null);
      try {
        await vfsApi.delete(ownerId, path);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [ownerId],
  );

  const deleteFolder = useCallback(
    async (path: string): Promise<number | null> => {
      if (!ownerId) return null;
      setIsDeleting(true);
      setError(null);
      try {
        const { deletedCount } = await vfsApi.deleteFolder(ownerId, path);
        return deletedCount;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        return null;
      } finally {
        setIsDeleting(false);
      }
    },
    [ownerId],
  );

  return { deleteFile, deleteFolder, isDeleting, error };
}
